import { Plus, Trash2 } from "lucide-react";
import { Group, ListField, NodeField, ParagraphsField, PlainField, SaveBar, getPath } from "@/admin/fields";

export const BlogTab = ({ content, update, saving, dirty, message, onSave }) => {
  const posts = getPath(content, ["blog", "posts"]);

  const sanitizeSlug = (value) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const addPost = () => {
    const stamp = Date.now().toString(36);
    const today = new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    update(["blog", "posts"], [
      ...posts,
      {
        slug: `new-post-${stamp}`,
        date: { text: today, size: null },
        title: { text: "New blog post", size: null },
        excerpt: { text: "A short teaser for this post.", size: null },
        paragraphs: ["Write the first paragraph of the article here."],
      },
    ]);
  };

  const removePost = (index) => {
    const title = getPath(content, ["blog", "posts", index, "title", "text"]);
    if (window.confirm(`Remove the post "${title}"? This cannot be undone after saving.`)) {
      update(["blog", "posts"], posts.filter((item, itemIndex) => itemIndex !== index));
    }
  };

  return (
    <div data-testid="admin-tab-content-blog">
      <SaveBar tabId="blog" saving={saving} dirty={dirty} message={message} onSave={onSave} />
      <Group title="Blog Listing Page" open>
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Title start" path={["blog", "titleStart"]} />
          <NodeField content={content} update={update} label="Title accent (green)" path={["blog", "titleAccent"]} />
        </div>
        <NodeField content={content} update={update} label="Intro" path={["blog", "intro"]} multiline />
        <NodeField content={content} update={update} label="Read more label" path={["blog", "readMoreLabel"]} />
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Banner heading" path={["blog", "banner", "heading"]} />
          <NodeField content={content} update={update} label="Banner text" path={["blog", "banner", "text"]} multiline />
        </div>
      </Group>

      <div className="af-blog-toolbar">
        <button type="button" className="af-btn af-btn-primary" onClick={addPost} data-testid="admin-add-post-btn">
          <Plus size={13} />
          Add Post
        </button>
        <span className="af-hint">New posts appear at the end of the blog listing once saved.</span>
      </div>

      {posts.map((post, index) => (
        <Group
          title={`Post ${index + 1}: ${getPath(content, ["blog", "posts", index, "title", "text"]) || post.slug}`}
          key={post.slug || index}
        >
          <PlainField
            label="URL slug (letters, numbers and hyphens; keep it unique)"
            value={post.slug}
            onChange={(value) => update(["blog", "posts", index, "slug"], sanitizeSlug(value))}
          />
          <NodeField content={content} update={update} label="Date" path={["blog", "posts", index, "date"]} />
          <NodeField content={content} update={update} label="Title" path={["blog", "posts", index, "title"]} />
          <NodeField content={content} update={update} label="Excerpt" path={["blog", "posts", index, "excerpt"]} multiline />
          <ParagraphsField
            label="Article paragraphs"
            items={getPath(content, ["blog", "posts", index, "paragraphs"])}
            onChange={(items) => update(["blog", "posts", index, "paragraphs"], items)}
          />
          <button
            type="button"
            className="af-btn af-btn-danger"
            onClick={() => removePost(index)}
            data-testid={`admin-remove-post-${index}`}
          >
            <Trash2 size={12} />
            Remove this post
          </button>
        </Group>
      ))}
    </div>
  );
};

const legalPages = [
  { slug: "privacy-policy", name: "Privacy Policy" },
  { slug: "terms-of-service", name: "Terms of Service" },
  { slug: "cookie-policy", name: "Cookie Policy" },
];

export const LegalTab = ({ content, update, saving, dirty, message, onSave }) => {
  return (
    <div data-testid="admin-tab-content-legal">
      <SaveBar tabId="legal" saving={saving} dirty={dirty} message={message} onSave={onSave} />
      {legalPages.map(({ slug, name }) => {
        const page = getPath(content, ["legal", slug]);
        return (
          <Group title={name} key={slug} open={slug === "privacy-policy"}>
            <div className="af-grid-2">
              <NodeField content={content} update={update} label="Title start" path={["legal", slug, "titleStart"]} />
              <NodeField content={content} update={update} label="Title accent (green)" path={["legal", slug, "titleAccent"]} />
            </div>
            <NodeField content={content} update={update} label="Last updated line" path={["legal", slug, "updated"]} />
            <NodeField content={content} update={update} label="Intro" path={["legal", slug, "intro"]} multiline />
            {page.sections.map((section, index) => (
              <Group title={`Section ${index + 1}: ${section.heading.text}`} key={index}>
                <NodeField content={content} update={update} label="Heading" path={["legal", slug, "sections", index, "heading"]} />
                <ParagraphsField
                  label="Paragraphs"
                  items={section.paragraphs}
                  onChange={(items) => update(["legal", slug, "sections", index, "paragraphs"], items)}
                />
                {section.bullets && section.bullets.length > 0 && (
                  <ListField
                    label="Bullets (one per line)"
                    items={section.bullets}
                    onChange={(items) => update(["legal", slug, "sections", index, "bullets"], items)}
                  />
                )}
              </Group>
            ))}
          </Group>
        );
      })}
    </div>
  );
};

export const ContactTab = ({ content, update, saving, dirty, message, onSave }) => {
  return (
    <div data-testid="admin-tab-content-contact">
      <SaveBar tabId="contact" saving={saving} dirty={dirty} message={message} onSave={onSave} />
      <Group title="Contact Page" open>
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Title start" path={["contact", "titleStart"]} />
          <NodeField content={content} update={update} label="Title accent (green)" path={["contact", "titleAccent"]} />
        </div>
        <NodeField content={content} update={update} label="Intro" path={["contact", "intro"]} multiline />
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Office hours" path={["contact", "hours"]} />
          <NodeField content={content} update={update} label="Address label" path={["contact", "addressLabel"]} />
        </div>
        <NodeField content={content} update={update} label="Address" path={["contact", "address"]} />
      </Group>
      <p className="af-hint-block">
        Email and phone shown on the Contact page come from the Global tab &rsquo;Contact Bar&rsquo; settings.
      </p>
    </div>
  );
};
